import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Identifying the Loop",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I don’t solve. I simply recognize: it’s the same argument, the same path."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I don’t solve. I simply recognize: it’s the same argument, the same path."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath.",
      "In my heart: “This is a loop. Not me.”"
    ],
      [
      "I pause with myself.",
      "One breath.",
      "In my heart: “This is a loop. Not me.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing — for me\nWhat is the topic that keeps coming back?",
      "What is the first sentence that usually ignites it?",
      "How does it usually end?"
    ],
      [
      "Writing — for me\nWhat is the topic that keeps coming back?",
      "What is the first sentence that usually ignites it?",
      "How does it usually end?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "When I recognize the loop — I already have power."
    ],
      [
      "Closing",
      "When I recognize the loop — I already have power."
    ]
    ),
    action: localizedGenderNode(
      "Once today: “This is a loop.” + one breath. I don’t continue the argument in my head.",
      "Once today: “This is a loop.” + one breath. I don’t continue the argument in my head."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "First Trigger: What Activates Me",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I see what activates me before I respond."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I see what activates me before I respond."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the trigger that sets me off? (word/tone/look/time/tiredness)",
      "What do I feel in the first second? (anger/hurt/fear/shame)",
      "What do I need in that moment that I don’t know how to say?"
    ],
      [
      "Writing\nWhat is the trigger that sets me off? (word/tone/look/time/tiredness)",
      "What do I feel in the first second? (anger/hurt/fear/shame)",
      "What do I need in that moment that I don’t know how to say?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Sometimes the argument is just a wrapper for a need."
    ],
      [
      "Closing",
      "Sometimes the argument is just a wrapper for a need."
    ]
    ),
    action: localizedGenderNode(
      "When the trigger rises: hand on chest + one breath before a word.",
      "When the trigger rises: hand on chest + one breath before a word."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "What I’m Really Trying to Get",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I ask: what do I really want — beneath the fight."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I ask: what do I really want — beneath the fight."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhen I enter this argument — what am I trying to get?",
      "Am I seeking justice, closeness, recognition, safety?",
      "What one simple request is hiding underneath?"
    ],
      [
      "Writing\nWhen I enter this argument — what am I trying to get?",
      "Am I seeking justice, closeness, recognition, safety?",
      "What one simple request is hiding underneath?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A clear request is better than a war."
    ],
      [
      "Closing",
      "A clear request is better than a war."
    ]
    ),
    action: localizedGenderNode(
      "I form one short request — just for myself.",
      "I form one short request — just for myself."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "What the Other Side Might Hear",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I check how it might sound from the other side."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I check how it might sound from the other side."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “Good intention can sound like an attack.”"
    ],
      [
      "I pause with myself.",
      "One breath. “Good intention can sound like an attack.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhen I say my sentences — how could they be heard?",
      "Which one of my sentences sounds like an accusation?",
      "How can I say it as “I feel…” instead of “You always…”?"
    ],
      [
      "Writing\nWhen I say my sentences — how could they be heard?",
      "Which one of my sentences sounds like an accusation?",
      "How can I say it as “I feel…” instead of “You always…”?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Soft language doesn’t mean I give up. It means I’m wise."
    ],
      [
      "Closing",
      "Soft language doesn’t mean I give up. It means I’m wise."
    ]
    ),
    action: localizedGenderNode(
      "Once today I replace “You…” with “I…”",
      "Once today I replace “You…” with “I…”"
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "The Point of No Return",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I identify when the argument is no longer about the topic."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I identify when the argument is no longer about the topic."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the sign we’ve reached the point of no return? (tone/yelling/contempt/shutdown)",
      "What do I usually do there?",
      "What would I like to do instead? (pause/water/space)"
    ],
      [
      "Writing\nWhat is the sign we’ve reached the point of no return? (tone/yelling/contempt/shutdown)",
      "What do I usually do there?",
      "What would I like to do instead? (pause/water/space)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Leaving the loop in time is maturity."
    ],
      [
      "Closing",
      "Leaving the loop in time is maturity."
    ]
    ),
    action: localizedGenderNode(
      "I choose one “stop word” for myself: “Pause.” Then one breath.",
      "I choose one “stop word” for myself: “Pause.” Then one breath."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "A Respectful Break (Not Abandonment)",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I learn to take a break without hurting."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I learn to take a break without hurting."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat scares me about stopping an argument in the middle?",
      "What does a respectful break sound like in one sentence?",
      "How long of a break do I need to return calmer?"
    ],
      [
      "Writing\nWhat scares me about stopping an argument in the middle?",
      "What does a respectful break sound like in one sentence?",
      "How long of a break do I need to return calmer?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A break protects the relationship. It’s not running away."
    ],
      [
      "Closing",
      "A break protects the relationship. It’s not running away."
    ]
    ),
    action: localizedGenderNode(
      "I write one fixed “break sentence” for myself.",
      "I write one fixed “break sentence” for myself."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "What I Bring from the Past",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I see what’s activated in me beyond the present."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I see what’s activated in me beyond the present."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhen in my life have I felt this way before? (no deep digging — just notice)",
      "What old wound is being activated in this loop?",
      "What did I need back then and didn’t receive?"
    ],
      [
      "Writing\nWhen in my life have I felt this way before? (no deep digging — just notice)",
      "What old wound is being activated in this loop?",
      "What did I need back then and didn’t receive?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Sometimes we fight in the present with an old pain."
    ],
      [
      "Closing",
      "Sometimes we fight in the present with an old pain."
    ]
    ),
    action: localizedGenderNode(
      "When it rises: “This is an old pain asking for safety.”",
      "When it rises: “This is an old pain asking for safety.”"
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Separating Behavior from Identity",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I stop turning the argument into “who you are.”"
    ],
      [
      "Daily intention:",
      "Daily intention: Today I stop turning the argument into “who you are.”"
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhich sentences turn it personal? (“You are this,” “You always”)",
      "How can I talk about behavior without attacking identity?",
      "What do I truly want to change in practice?"
    ],
      [
      "Writing\nWhich sentences turn it personal? (“You are this,” “You always”)",
      "How can I talk about behavior without attacking identity?",
      "What do I truly want to change in practice?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Behavior can change. Humiliation destroys."
    ],
      [
      "Closing",
      "Behavior can change. Humiliation destroys."
    ]
    ),
    action: localizedGenderNode(
      "I practice this sentence: “I’m talking about what happened, not who you are.”",
      "I practice this sentence: “I’m talking about what happened, not who you are.”"
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "My Boundary in an Argument",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I define what’s not acceptable for me during a fight."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I define what’s not acceptable for me during a fight."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is my boundary in an argument? (yelling/insults/shutdown/threats)",
      "What do I do when it’s crossed?",
      "What am I willing to do to protect this boundary?"
    ],
      [
      "Writing\nWhat is my boundary in an argument? (yelling/insults/shutdown/threats)",
      "What do I do when it’s crossed?",
      "What am I willing to do to protect this boundary?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "A boundary is not punishment. It’s protection."
    ],
      [
      "Closing",
      "A boundary is not punishment. It’s protection."
    ]
    ),
    action: localizedGenderNode(
      "I write one clear boundary in one sentence.",
      "I write one clear boundary in one sentence."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "One Request Instead of a List",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I ask for one clear thing — not ten."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I ask for one clear thing — not ten."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the most important request I have inside this loop?",
      "How does it sound short, clear, without blame?",
      "What am I willing to give in return? (language/time/effort)"
    ],
      [
      "Writing\nWhat is the most important request I have inside this loop?",
      "How does it sound short, clear, without blame?",
      "What am I willing to give in return? (language/time/effort)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "One real request creates a chance for movement."
    ],
      [
      "Closing",
      "One real request creates a chance for movement."
    ]
    ),
    action: localizedGenderNode(
      "I practice saying my request out loud to myself once.",
      "I practice saying my request out loud to myself once."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Repair After a Fight",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I learn how to repair without erasing."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I learn how to repair without erasing."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is the most hurtful thing I said or did in this loop?",
      "How can I take responsibility without justifying?",
      "What one repair sentence would I want to hear?"
    ],
      [
      "Writing\nWhat is the most hurtful thing I said or did in this loop?",
      "How can I take responsibility without justifying?",
      "What one repair sentence would I want to hear?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Repair builds trust."
    ],
      [
      "Closing",
      "Repair builds trust."
    ]
    ),
    action: localizedGenderNode(
      "I write one short “repair sentence.”",
      "I write one short “repair sentence.”"
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Choosing the Right Time to Talk",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I stop trying to fix things when we’re exhausted."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I stop trying to fix things when we’re exhausted."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhen do we usually fight? (night/tired/stressed)",
      "When is there more chance for a good conversation?",
      "How can I suggest another time without sounding like I’m avoiding?"
    ],
      [
      "Writing\nWhen do we usually fight? (night/tired/stressed)",
      "When is there more chance for a good conversation?",
      "How can I suggest another time without sounding like I’m avoiding?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Timing is half the solution."
    ],
      [
      "Closing",
      "Timing is half the solution."
    ]
    ),
    action: localizedGenderNode(
      "I choose one possible “conversation window” for this week.",
      "I choose one possible “conversation window” for this week."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "What We Truly Need as a Couple",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I look at the need beneath the loop."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I look at the need beneath the loop."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat is missing between us that fuels the loop? (safety/listening/time/respect)",
      "What works between us when it’s good?",
      "What one small change could improve 10%?"
    ],
      [
      "Writing\nWhat is missing between us that fuels the loop? (safety/listening/time/respect)",
      "What works between us when it’s good?",
      "What one small change could improve 10%?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "It doesn’t take a revolution. Sometimes 10% is enough."
    ],
      [
      "Closing",
      "It doesn’t take a revolution. Sometimes 10% is enough."
    ]
    ),
    action: localizedGenderNode(
      "I choose one small change for this week.",
      "I choose one small change for this week."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Closing: A Return Sentence for the Next Loop",
    opening: localizedGenderNode(
      [
      "Daily intention:",
      "Daily intention: Today I leave with a sentence that brings me back to myself in real time."
    ],
      [
      "Daily intention:",
      "Daily intention: Today I leave with a sentence that brings me back to myself in real time."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Writing\nWhat did I learn about this loop?",
      "What is the earliest sign I’m entering it?",
      "Return sentence to “the moment before” (short, pocket-sized):"
    ],
      [
      "Writing\nWhat did I learn about this loop?",
      "What is the earliest sign I’m entering it?",
      "Return sentence to “the moment before” (short, pocket-sized):"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Final closure of the journey",
      "I don’t have to win the argument to be safe.",
      "I want a relationship with respect — even when it hurts.",
      "And I start with this: one pause. One breath. One fewer word."
    ],
      [
      "Final closure of the journey",
      "I don’t have to win the argument to be safe.",
      "I want a relationship with respect — even when it hurts.",
      "And I start with this: one pause. One breath. One fewer word."
    ]
    ),
    action: localizedGenderNode(
      "Once today: I choose quiet over reaction.",
      "Once today: I choose quiet over reaction."
    ),
  },
].map(buildNormalizedDay);

export const repeatingConflictsInRelationshipBlueprintEn = buildNormalizedPilotBlueprint({
  id: "repeating_conflicts_in_relationship_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "repeating_conflicts_in_relationship",
  category: "general",
  journeyName: {
    he: "מריבות חוזרות (אותו סיפור שוב ושוב)",
    en: "Repeating Conflicts (The Same Story Again and Again)",
    es: "Conflictos repetidos (La misma historia una y otra vez)",
  },
  days,
});

export default repeatingConflictsInRelationshipBlueprintEn;
